import {CommonModule} from "@angular/common";
import {booleanAttribute, Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from "@angular/core";
import {MatChipsModule} from "@angular/material/chips";
import {MatIconModule} from "@angular/material/icon";
import {MatTooltipModule} from "@angular/material/tooltip";

import {PluralizedValues, UtilsService} from "framework/services/utils.service";
import {ColorChipComponent} from "framework/ui/chips/color-chip.component";
import {BaseComponent} from "framework/ui/components";
import {KeyValueListTooltipComponent} from "framework/ui/components/tooltip/library/key-value-list/tooltip.component";
import {ListTooltipComponent} from "framework/ui/components/tooltip/library/list/tooltip.component";
import {TooltipComponentDirective} from "framework/ui/components/tooltip/tooltip.component";
import {TooltipWhenTruncatedDirective} from "framework/ui/shared/tooltip-when-truncated.directive";
import {CoreStackBlue} from "framework/ui/charting/core/colors";

@Component({
    selector: "cs-key-value-chip-list",
    templateUrl: "./key-value-chip-list.component.html",
    styleUrls: ["./key-value-chip-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ColorChipComponent,
        CommonModule,
        KeyValueListTooltipComponent,
        ListTooltipComponent,
        MatChipsModule,
        MatTooltipModule,
        MatIconModule,
        TooltipComponentDirective,
        TooltipWhenTruncatedDirective
    ]
})
export class KeyValueChipListComponent extends BaseComponent
{
    @Input() chipName: PluralizedValues;
    @Input() chips: KeyValueChip[];
    @Input({transform: booleanAttribute}) noWrap: boolean;
    @Input() collapseThreshold: number;
    @Input() innerCollapseThreshold: number;
    @Input({transform: booleanAttribute}) removable: boolean      = false;
    @Input({transform: booleanAttribute}) clickable: boolean      = false;
    @Input() maxWidthPx: number                                   = 350;
    @Input() sizeClass: string;
    @Input({transform: booleanAttribute}) tooltipEnabled: boolean = false;
    @Input() maxKeyWidth: number                                  = 140;
    @Input() maxValueWidthDefault: number;
    @Input({transform: booleanAttribute}) whiteSpaceTagGovernance: boolean = false;
    @Output() chipRemoved  = new EventEmitter<KeyValueChip>();
    @Output() chipSelected = new EventEmitter<KeyValueChip>();

    readonly collapsedColor: string = CoreStackBlue.blue200.hex;

    get collapsedText(): string
    {
        if (!this.chipName?.valid) return `+${this.chips.length}`;
        return `${this.chips.length} ${UtilsService.pluralize(this.chips.length, this.chipName)}...`;
    }

    get maxValueWidth(): number
    {
        if (this.maxValueWidthDefault) return this.maxValueWidthDefault;
        return this.maxWidthPx - this.maxKeyWidth - 45;
    }

    get tooltipDelay(): number
    {
        if (this.tooltipEnabled)
        {
            return 500;
        }

        return 0;
    }
    
    chipValue(chip: KeyValueChip): string
    {
        if (chip.tooltipItems.length > this.innerCollapseThreshold)
        {
            return `+${chip.tooltipItems.length}`;
        }
        else
        {
            return chip.value || "";
        }
    }

    remove(chip: KeyValueChip): void
    {
        const index = this.chips.indexOf(chip);
        if (index >= 0)
        {
            this.chips.splice(index, 1);
        }

        this.chipRemoved.emit(chip);
    }

    toggleChip(chip: KeyValueChip): void
    {
        this.chipSelected.emit(chip);
    }
}

export class KeyValueChip
{
    constructor(public readonly key: string,
                public readonly value: string,
                public readonly tooltipItems?: string[],
                public readonly separator?: string,
                public selected?: boolean)
    {
        this.key ||= "";
        this.value ||= "";
        this.tooltipItems ||= [];
        this.separator ||= "";
        this.selected ||= false;
    }
}
